import { singleton } from "tsyringe";
import { CdpClient } from "../cdp/CdpClient";
import { invokeWhenProfileSetup, updateProfileProperties } from "../cdp/BlueConicUtils";
import { Logger } from "../utils/Logger";
import { TimelineEventIds } from "../cdp/TimelineEventsId";
import { MorduTimelineEventProperties } from "./mordu/MorduTimelineEventProperties";
import { MorduPage } from "./mordu/MorduPage";
import { csvToArray } from "../utils/StringUtils";
import { BlueConicProfileProperties } from "../cdp/BlueConicProfileProperties";
import { isValidPostalCode, normalizePostalCode } from "../utils/PostalCode";
import { getUserFromDataLayer } from "../gtm/GtmUtils";
import { PageAttributes } from "../shared/PageAttributes";

/*/
 * CDP client for sites inside the DotCa GTM container.
 */
@singleton()
export class DotCaCdpClient extends CdpClient {
  public sendMorduTimelineEvent(
    eventType: TimelineEventIds.RcMorduComment | TimelineEventIds.RcMorduScore | TimelineEventIds.RcMorduBookmark,
  ): void {
    const timelineAttributes = this.getMorduTimelineEventProperties();
    if (!timelineAttributes) {
      return;
    }
    const now = new Date();
    invokeWhenProfileSetup(() => {
      Logger.verbose(`Sending ${eventType}`, timelineAttributes);
      blueConicClient.profile.getProfile().timeline.createEvent(eventType, now, timelineAttributes, undefined);
    });
  }

  /**
   * Collect the timeline event properties for the PSI module events and send the event to BlueConic.
   * @param value The action value
   * @param variant The variant value
   */
  public sendActionPSITimeLineEvent(value: string | undefined, variant: string | undefined): void {
    const timelineAttributes = this.getActionPSITimelineEventProperties(value, variant);
    if (!timelineAttributes) {
      return;
    }
    const now = new Date();
    invokeWhenProfileSetup(() => {
      Logger.verbose(`Sending ${TimelineEventIds.RcActionPSI}`, timelineAttributes);
      blueConicClient.profile
        .getProfile()
        .timeline.createEvent(TimelineEventIds.RcActionPSI, now, timelineAttributes, undefined);
    });
  }

  /**
   * Get the timeline event properties for the PSI module events.
   * @param value The action value
   * @param variant The variant value
   * @private
   */
  private getActionPSITimelineEventProperties(
    value: string | undefined,
    variant: string | undefined,
  ): PageAttributes | undefined {
    const attributes = {
      ...this.pageAttributesExtractor.extractPageLoadedAttributes(),
      variant,
      betaPSIAction: value,
    };
    if (!attributes) {
      Logger.warn("Could not get page attributes from the DataLayer.");
      return undefined;
    }
    return attributes;
  }

  /**
   * Set the user's regional postal code in the profile.
   */
  public setUserRegionalPostalCode(): void {
    invokeWhenProfileSetup(() => {
      this.handleSetUserRegionalPostalCode();
    });
  }

  private getMorduTimelineEventProperties(): MorduTimelineEventProperties | undefined {
    const pageAttributes = dataLayerHelper.get<MorduPage>("page");
    if (!pageAttributes) {
      Logger.warn("Could not get Mordu's page attributes from the DataLayer.");
      return undefined;
    }
    return {
      allergenes: csvToArray(pageAttributes.Allergene),
      dossier: csvToArray(pageAttributes.Dossier),
      groupeSection: csvToArray(pageAttributes.GroupeSection),
      etiquette: csvToArray(pageAttributes.Etiquette),
      section: csvToArray(pageAttributes.Section),
      source: csvToArray(pageAttributes.Source),
      sousThemes: csvToArray(pageAttributes.SousTheme),
      theme: csvToArray(pageAttributes.Theme),
      typeContenu: csvToArray(pageAttributes.TypeContenu),
    };
  }

  /**
   * Set the user's regional postal code in the profile.
   * This method retrieves the postal code from the DataLayer, normalizes it,
   * validates it, and updates the user's profile properties in BlueConic.
   * If the postal code is not found or is invalid, appropriate error messages
   * are logged.
   */
  private handleSetUserRegionalPostalCode(): void {
    let regionPostalCode = getUserFromDataLayer()?.postalCode;
    if (!regionPostalCode) {
      Logger.error("No postal code found in the DataLayer");
      return;
    }
    regionPostalCode = normalizePostalCode(regionPostalCode);
    if (!isValidPostalCode(regionPostalCode)) {
      Logger.error(`Postal code is invalid: ${regionPostalCode}`);
      return;
    }

    const fsa = regionPostalCode.slice(0, 3);
    updateProfileProperties([
      {
        propertyId: BlueConicProfileProperties.postalCodeRegion,
        value: regionPostalCode,
      },
      {
        propertyId: BlueConicProfileProperties.fsaCodeRegion,
        value: fsa,
      },
    ]);
    const profile = blueConicClient.profile.getProfile();
    const postalCodeDeclared = profile.getValue(BlueConicProfileProperties.postalCodeDeclared);
    if (!postalCodeDeclared) {
      Logger.verbose(`CDP: Setting GroupId Postal Code as ${regionPostalCode}`);
      updateProfileProperties([
        {
          propertyId: BlueConicProfileProperties.postalCodeGroupId,
          value: regionPostalCode,
        },
        {
          propertyId: BlueConicProfileProperties.fsaGroupId,
          value: fsa,
        },
      ]);
    }
  }
}
